/**
 * 견적 문구 복사 — 카카오톡 등에 붙여넣을 텍스트 생성.
 *
 * 포맷(품목별, 구성 품목 순서대로):
 *   [파트넘버] [디스크립션]
 *   [컨디션-문구], 단가 : [판매단가]원 (vat별도)
 *   - [컨디션-워런티]
 *   - 딜리버리 7-8일 소요
 *
 * 컨디션-문구 맵핑 (2026-07 현민 스펙):
 *   F/S=신품, F/S Spare=신품 스페어, NOB=신품(박스오픈),
 *   Newbulk=신품 벌크, Newpull=신품 벌크(탈거), Bulk=벌크, REF=중고
 * 컨디션-워런티: REF=워런티 90일, 그 외 모두=워런티 1년
 */
import { splitConditions, conditionGroup } from './condition';

// 정규화 키(lowercase.trim) → 문구. 미매핑 코드는 3그룹(신품/벌크/중고)로 폴백.
const CONDITION_PHRASE: Record<string, string> = {
  'f/s':       '신품',
  'fs':        '신품',
  'f/s spare': '신품 스페어',
  'nob':       '신품(박스오픈)',
  'newbulk':   '신품 벌크',
  'new bulk':  '신품 벌크',
  'newpull':   '신품 벌크(탈거)',
  'new pull':  '신품 벌크(탈거)',
  'bulk':      '벌크',
  'ref':       '중고',
};

/** 컨디션 코드(멀티값이면 첫 값) → 표기 문구. */
export function conditionPhrase(raw: string | null | undefined): string {
  const first = (splitConditions(raw)[0] ?? '').trim();
  const key = first.toLowerCase();
  if (CONDITION_PHRASE[key]) return CONDITION_PHRASE[key];
  const g = conditionGroup(first);                 // 신품/벌크/중고/미확인 폴백
  return g === '미확인' ? (first || '미확인') : g;
}

/** 컨디션 → 워런티 문구. REF 만 90일, 나머지 전부 1년(스펙). */
export function conditionWarranty(raw: string | null | undefined): string {
  const first = (splitConditions(raw)[0] ?? '').trim().toLowerCase();
  return first === 'ref' ? '워런티 90일' : '워런티 1년';
}

export interface QuoteCopyItem {
  optionPart:   string | null;
  description:  string | null;
  unitPriceKrw: number | null;
  condition:    string | null;    // 딜 품목 requested_condition
  /**
   * 서버면 'server'. 미지정은 'part'(기존 동작).
   *
   * 서버는 구성품을 한 줄씩 나열하지 않는다 — 카톡에 붙이면 20줄이 넘어가고,
   * 고객이 볼 형태도 아니다. 대신 **약식 요약** 한 블록으로 낸다.
   */
  kind?:        'server' | 'part';
  /** 서버 약식 요약(여러 줄). 서버 헤더의 저장된 item_description 이 정본. */
  summary?:     string | null;
}

/**
 * 견적 품목 배열 → 붙여넣기용 문구(품목 블록 사이 빈 줄 — 카톡 가독성).
 *
 * 개별 품목:
 *   [파트넘버] [디스크립션]
 *   [컨디션], 단가 : [판매단가]원 (vat별도)
 *   - [워런티]
 *   - 딜리버리 7-8일 소요
 *
 * 서버:
 *   [약식 요약 — 모델명 + CPU/RAM/DISK/… 여러 줄]
 *   (빈 줄)
 *   [컨디션], 단가 : [서버 1대 판매가]원 (vat별도)
 *   딜리버리 7-8일 소요
 *
 *   워런티 줄을 따로 넣지 않는다 — 워런티는 구성에 NON-HW 로 직접 넣는 항목이라
 *   이미 요약 안에 들어 있다(안 넣었으면 안 나오는 게 맞다).
 */
export function formatQuoteCopyText(items: QuoteCopyItem[]): string {
  return items
    .map((it) => {
      const price = it.unitPriceKrw != null ? it.unitPriceKrw.toLocaleString('ko-KR') : '-';
      const priceLine = `${conditionPhrase(it.condition)}, 단가 : ${price}원 (vat별도)`;

      if (it.kind === 'server') {
        const summary = (it.summary ?? it.description ?? '').trim();
        return [summary, '', priceLine, '딜리버리 7-8일 소요']
          .filter((l, i) => l !== '' || i === 1)   // 빈 줄은 요약 다음 한 줄만
          .join('\n');
      }

      const head = [(it.optionPart ?? '').trim(), (it.description ?? '').trim()]
        .filter(Boolean)
        .join(' ');
      return [
        head,
        priceLine,
        `- ${conditionWarranty(it.condition)}`,
        '- 딜리버리 7-8일 소요',
      ].join('\n');
    })
    .join('\n\n');   // 품목 사이 빈 줄
}


// ─── 임대 견적 문구 ──────────────────────────────────────────────────
//
// ★ 판매용 `formatQuoteCopyText` 와 **한 함수로 합치지 않는다.**
//   두 문구는 줄 구성이 통째로 다르다 — 판매는 컨디션·워런티를 말하고
//   임대는 기간·월 금액·계약 총액을 말한다. 한 함수에 `if (임대)` 를 넣으면
//   판매 문구를 고칠 때마다 임대가 같이 흔들리고, 반대도 마찬가지다.
//   견적 계산에서 quote-math / quote-rental 을 갈라 둔 것과 같은 선이다.

export interface RentalQuoteCopyItem {
  optionPart:  string | null;
  description: string | null;
  /** 서버면 'server'. 서버는 구성품을 나열하지 않고 약식 요약 한 블록으로 낸다. */
  kind?:       'server' | 'part';
  /** 서버 약식 요약(여러 줄). 정본은 서버 헤더행의 item_description. */
  summary?:    string | null;
  /** 임대기간 N (개월). */
  months:      number | null;
  quantity:    number | null;
  /** 적용 임대단가 — 저장된 unit_price_krw. */
  unitPriceKrw: number | null;
}

const krw = (n: number | null): string => (n == null ? '-' : n.toLocaleString('ko-KR'));

/**
 * 임대 견적 품목 배열 → 붙여넣기용 문구 (2026-08-20 현민 지정 포맷).
 *
 *   [약식 요약 — 모델명 + CPU/RAM/DISK/… 여러 줄]
 *   (빈 줄)
 *   임대 기간 : 12개월
 *   수량 : 2대
 *   임대 단가 : 1,000,000원 (vat 별도)
 *   월 임대 금액 : 2,000,000원 (vat 별도)
 *   금액 합계 : 24,000,000원 (vat 별도)
 *   딜리버리 7~8일 소요
 *
 * 개별 파트는 첫 줄이 `[파트넘버] [디스크립션]` 한 줄이라 빈 줄을 넣지 않는다
 * (판매 문구의 파트 블록과 같은 규칙 — 빈 줄은 여러 줄 요약 뒤에만 둔다).
 *
 * ★ 컨디션·워런티 줄이 없다. 임대 견적서 시트에도 상태 열이 없고(구매 시트의
 *   E열이 임대에서는 기간으로 바뀐다), 워런티는 임대 계약 조건이지 품목 속성이
 *   아니다. 없는 것을 지어내지 않는다.
 *
 * ★ 수량 단위는 서버 '대', 파트 'EA' 다 — 화면에서 쓰는 어휘 그대로다
 *   (서버 헤더행은 대수, 품목 행은 EA).
 *
 * 금액은 화면·견적서와 같은 식으로 만든다.
 *   월 임대 금액 = 임대 단가 × 수량
 *   금액 합계    = 월 임대 금액 × 기간
 * 셋 중 하나라도 없으면 그 줄만 '-' 로 두고 나머지는 그대로 낸다 — 블록을
 * 통째로 지우면 어느 값이 비었는지 알 수 없다.
 */
export function formatRentalQuoteCopyText(items: RentalQuoteCopyItem[]): string {
  return items
    .map((it) => {
      const months = it.months   != null && it.months   > 0 ? it.months   : null;
      const qty    = it.quantity != null && it.quantity > 0 ? it.quantity : null;
      const unit   = it.unitPriceKrw != null && it.unitPriceKrw > 0 ? it.unitPriceKrw : null;
      const monthly = unit != null && qty != null ? unit * qty : null;
      const total   = monthly != null && months != null ? monthly * months : null;

      const body = [
        `임대 기간 : ${months != null ? `${months}개월` : '-'}`,
        `수량 : ${qty != null ? `${qty}${it.kind === 'server' ? '대' : 'EA'}` : '-'}`,
        `임대 단가 : ${krw(unit)}원 (vat 별도)`,
        `월 임대 금액 : ${krw(monthly)}원 (vat 별도)`,
        `금액 합계 : ${krw(total)}원 (vat 별도)`,
        '딜리버리 7~8일 소요',
      ];

      if (it.kind === 'server') {
        const summary = (it.summary ?? it.description ?? '').trim();
        return [summary, '', ...body].join('\n');
      }
      const head = [(it.optionPart ?? '').trim(), (it.description ?? '').trim()]
        .filter(Boolean)
        .join(' ');
      return [head, ...body].join('\n');
    })
    .join('\n\n');   // 품목 사이 빈 줄
}
